import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "react-toastify";
import { API_DETAILS, COOKIE_KEYS } from "../App";
import "../styles/PricingPage.css";
import "../styles/Home.css";

const PricingPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(""); // ✅ track selected plan
  const [apiPlans, setApiPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  // Plan ids live in the database alongside the Stripe billing details. Reading
  // them here is what lets a returning patient go straight from this page to
  // payment, instead of being sent to their profile to pick the plan again.
  useEffect(() => {
    axios
      .get(API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + "/subscription-plans")
      .then((res) => setApiPlans(res.data || []))
      .catch(() => setApiPlans([]));
  }, []);

  // An existing patient whose membership lapsed already has an account, so the
  // sign-up flow is a dead end for them. This mirrors the criterion the patient
  // route guard already uses, read-only — no auth behaviour is changed here.
  // Matching userType too keeps providers and admins on the sign-up path rather
  // than sending them to a route that would bounce them straight back out.
  const userId = Cookies.get(COOKIE_KEYS.userId);
  const hasExistingSession =
    Boolean(Cookies.get(COOKIE_KEYS.token)) &&
    Cookies.get(COOKIE_KEYS.userType) === "PATIENT" &&
    Boolean(userId);

  const plans = [
    {
      name: "Youro One",
      apiName: "STANDARD",
      price: "$599",
      period: "/year",
      badge: "",
      features: [
        "1 diagnosis treated",
        "4 video appointments with your urology-trained provider annually",
        "Symptom Score calculation before each appointment",
        "Direct messaging with your provider",
        "Prescription refills",
        "Curated educational content",
      ],
    },
    {
      name: "Youro Plus",
      apiName: "PREMIUM",
      price: "$899",
      period: "/year",
      badge: "Most Popular",
      features: [
        "Multiple diagnoses treated",
        "8 video appointments with your urology-trained provider annually",
        "Symptom Score calculation before each appointment",
        "Direct messaging with your provider",
        "Prescription refills",
        "Curated educational content",
      ],
    },
    {
      name: "Youro On Demand",
      apiName: "ONETIME",
      price: "$199",
      period: "once",
      badge: "",
      features: [
        "1 diagnosis treated",
        "1 video appointment",
        "Direct messaging with your provider for 3 months",
        "Curated educational content",
      ],
    },
  ];

  const isCTAEnabled = !!selectedPlan;

  /**
   * A signed-in patient goes straight from here to Stripe. Sending them to their
   * profile instead meant re-picking the plan they had just chosen before any
   * payment button appeared — the same choice made twice.
   *
   * Someone without an account still has to register first, since a checkout has
   * to be attached to a patient.
   */
  const renewOrSignUp = async () => {
    if (!isCTAEnabled || busy) return;

    if (!hasExistingSession) {
      navigate("/signup", { state: { selectedPlan } });
      return;
    }

    const chosen = plans.find((p) => p.name === selectedPlan);
    const match = apiPlans.find((p) => p.name === chosen?.apiName);
    if (!match) {
      toast.error("We couldn't load that plan just now. Please refresh and try again.");
      return;
    }

    setBusy(true);
    try {
      const res = await axios.post(
        API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          `/payment/create-checkout-session?userId=${encodeURIComponent(userId)}&planId=${encodeURIComponent(match.planId)}`
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("We couldn't open the payment page. Please try again.");
        setBusy(false);
      }
    } catch (err) {
      // The server refuses plans the patient isn't eligible for — for instance a
      // one-time pass when their membership is still running — and its message
      // explains why, so show that rather than a generic failure.
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "We couldn't start checkout. Please try again."
      );
      setBusy(false);
    }
  };

  return (
    <div className="pricing-page">
      <header className="home-nav">
        <Link to="/" className="home-nav-left" aria-label="Youro home">
          <div className="home-nav-logo-circle">Y</div>
          <span className="home-nav-logo-text">Youro</span>
        </Link>
        <nav className="home-nav-center">
          <button
            className="home-nav-link"
            onClick={() => navigate("/about")}
          >
            About
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/conditions")}
          >
            Find Your Diagnosis
          </button>
          <button
            className="home-nav-link"
            onClick={() => navigate("/assessment")}
          >
            Symptom Assessment
          </button>
        </nav>
        <div className="home-nav-right">
          <button
            className="home-nav-signin"
            onClick={() => navigate("/login")}
          >
            Sign In
          </button>
          <button
            className="home-nav-get-started"
            onClick={() => navigate("/signup")}
          >
            Get Started
          </button>
        </div>
      </header>

      <div className="pricing-content">
        <button className="pricing-back" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <h1 className="pricing-title">Choose Your Care Plan</h1>

        <p className="pricing-subtitle">
          Select the membership that best fits your urologic health needs. All plans include access
          to our expert urology-trained providers.
        </p>

        <div className="pricing-grid">
          {plans.map((p) => {
            const isSelected = selectedPlan === p.name;

            return (
              <div
                key={p.name}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                className={[
                  "pricing-card",
                  p.badge ? "popular" : "",
                  isSelected ? "selected" : "",
                ].join(" ")}
                onClick={() => setSelectedPlan(p.name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedPlan(p.name);
                }}
              >
                {p.badge ? <div className="pricing-badge">{p.badge}</div> : null}

                <h2 className="plan-name">{p.name}</h2>

                <div className="plan-price">
                  <span className="price">{p.price}</span>
                  <span className="period">{p.period}</span>
                </div>

                <ul className="plan-features">
                  {p.features.map((f) => (
                    <li key={f} className="feature">
                      <span className="check">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="pricing-footer">
          <button
            className="pricing-cta"
            disabled={!isCTAEnabled || busy}
            onClick={renewOrSignUp}
          >
            {busy
              ? "Opening secure payment…"
              : hasExistingSession
              ? "Continue to Payment"
              : "Continue to Sign Up"}{" "}
            <span className="arrow">➜</span>
          </button>

          {/* ✅ sentence below CTA */}
          <p className="pricing-footer-note">
            All plans include secure messaging, prescription management, and access to urology-trained providers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
